import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/ApiManager';
import Navbar from '../components/Navbar';
import { theme } from '../utils/theme';

const EmailVerification = () => {
  const { t } = useTranslation('verification');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error

  useEffect(() => {
    let isMounted = true;
    
    const verifyEmail = async () => {
      try {
        const token = searchParams.get('token');
        const userId = searchParams.get('userId');

        if (!token || !userId) {
          if (isMounted) {
            setStatus('error');
          }
          return;
        }

        console.log('Verification params:', { token, userId });
        
        // Make the API call to verify email
        const response = await api.get(`/api/auth/verify-email?token=${token}&userId=${userId}`);
        
        console.log('Raw verification response:', response);
        
        // Force status update to success regardless of response details
        if (isMounted) {
          console.log('Forcing status to success');
          setStatus('success');
          
          // Use a direct timeout for redirect
          setTimeout(() => {
            console.log('Redirecting to login page');
            navigate('/login', { 
              state: { 
                message: t('verificationSuccess'),
                type: 'success'
              } 
            });
          }, 2000);
        }
      } catch (error) {
        console.error('Email verification error details:', error);
        
        // Handle "already verified" error as success
        if (error.response?.data?.error === 'Email already verified') {
          if (isMounted) {
            console.log('Already verified - setting success');
            setStatus('success');
            
            setTimeout(() => {
              navigate('/login', { 
                state: { 
                  message: t('verificationSuccess'),
                  type: 'success'
                } 
              });
            }, 2000);
          }
        } else {
          // Any other error
          if (isMounted) {
            console.log('Setting error state');
            setStatus('error');
          }
        }
      }
    };

    // Short delay before verification
    const timer = setTimeout(() => {
      verifyEmail();
    }, 500);
    
    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [searchParams, navigate, t]);

  return (
    <div className="min-h-screen bg-background-primary">
      <Navbar />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-6">
          <div className="text-center">
            {status === 'verifying' && (
              <>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <h2 className="mt-4 text-xl font-semibold text-gray-900">{t('verifying')}</h2>
                <p className="mt-2 text-sm text-gray-500">Current status: {status}</p>
              </>
            )}
            
            {status === 'success' && (
              <>
                <div className="mx-auto h-12 w-12 text-green-500">
                  <svg className="h-full w-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="mt-4 text-xl font-semibold text-gray-900">{t('success')}</h2>
                <p className="mt-2 text-gray-600">{t('redirecting')}</p>
              </>
            )}
            
            {status === 'error' && (
              <>
                <div className="mx-auto h-12 w-12 text-red-500">
                  <svg className="h-full w-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h2 className="mt-4 text-xl font-semibold text-gray-900">{t('error')}</h2>
                <p className="mt-2 text-gray-600">{t('errorMessage')}</p>
                <button
                  onClick={() => navigate('/login')}
                  className={`${theme.button.base} ${theme.button.primary} mt-4`}
                >
                  {t('backToLogin')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;